/**
 * Shared React Flow store selectors for node components.
 *
 * Define selectors at module scope so each is a stable reference —
 * avoids unnecessary `useStore` re-subscriptions and lets the
 * selector identity be shared across all consumers.
 */

import { getIsMultiSelectSync } from "../canvas/utils/multi-select-store";

const selectedByIdCache = new Map<string, boolean>();
const lockedByIdCache = new Map<string, boolean>();
const measuredHeightByIdCache = new Map<string, unknown>();

function isCanvasPositionInteracting(): boolean {
  try {
    return Boolean((window as any).__flowmuseCanvasPositionInteracting);
  } catch {
    return false;
  }
}

// Backwards-compat selector. Reads from the external multi-select store that
// canvas-root publishes once per real selection change. Avoids scanning the
// nodes array on every ReactFlow store update.
//
// Prefer `useIsMultiSelect()` from `components/canvas/utils/multi-select-store`
// directly when adding new code — it subscribes via useSyncExternalStore and
// doesn't re-run on unrelated ReactFlow store updates.
export const selectIsMultiSelect = (_s: any): boolean => {
  try {
    return getIsMultiSelectSync();
  } catch {
    return false;
  }
};

export const selectNodeSelectedById = (id: string) => (s: any): boolean => {
  const key = String(id);
  if (isCanvasPositionInteracting() && selectedByIdCache.has(key)) {
    return Boolean(selectedByIdCache.get(key));
  }
  const value = Boolean(s?.nodeLookup?.get?.(key)?.selected);
  selectedByIdCache.set(key, value);
  return value;
};

export const selectNodeLockedById = (id: string) => (s: any): boolean => {
  const key = String(id);
  if (isCanvasPositionInteracting() && lockedByIdCache.has(key)) {
    return Boolean(lockedByIdCache.get(key));
  }
  const value = Boolean(s?.nodeLookup?.get?.(key)?.data?.locked);
  lockedByIdCache.set(key, value);
  return value;
};

export const selectNodeMeasuredHeightById =
  (id: string) =>
  (s: any): unknown => {
    const key = String(id);
    if (isCanvasPositionInteracting() && measuredHeightByIdCache.has(key)) {
      return measuredHeightByIdCache.get(key);
    }
    const value = s?.nodeLookup?.get?.(key)?.measured?.height;
    measuredHeightByIdCache.set(key, value);
    return value;
  };
